import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Separator } from './ui/separator';
import { XIcon } from '../assets/X';
import { CheckIcon } from '../assets/Check';
import { useChangeLanguage, type AppLanguage } from '../lib/useChangeLanguage';
import {
  LANGUAGE_DRAWER_LABELS,
  LANGUAGE_NAMES,
} from '../utils/languageLabels';
import { notificationOccurred, selectionChanged } from '../utils/hapticEvents';

const LANGUAGES = ['uz', 'ru', 'en'];

type IProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Вызывается после сохранения — экрану нужно обновить выбранный язык. */
  onSaved?: (language: string) => void;
  /**
   * Применять по тапу и сразу закрываться, без «Сохранить». Так работает
   * быстрый переключатель в шапке; в настройках остаётся кнопка.
   */
  applyOnSelect?: boolean;
};

/**
 * Выбор языка приложения. В настройках язык переключается только по
 * «Сохранить»: до этого меняется лишь подсветка в списке, поэтому i18n не
 * дёргается на каждый тап. Из шапки — сразу по тапу (applyOnSelect).
 */
export const LanguageDrawer = ({
  open,
  setOpen,
  onSaved,
  applyOnSelect = false,
}: IProps) => {
  const { t } = useTranslation();
  const [lang, setLang] = useState(localStorage.getItem('language') || 'uz');

  const applyLanguage = useChangeLanguage();

  const apply = (next: string) => {
    try {
      applyLanguage(next as AppLanguage);
      onSaved?.(next);
      setOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
      notificationOccurred('error');
    }
  };

  const changeLanguage = () => apply(lang);

  const pick = (item: string) => {
    setLang(item);
    selectionChanged();
    if (applyOnSelect) apply(item);
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent className="bg-fill-background">
        <div className="px-4 flex flex-row justify-between items-center pb-2">
          <DrawerTitle>
            {LANGUAGE_DRAWER_LABELS[lang]?.title ?? t('language')}
          </DrawerTitle>
          <div
            className="flex items-center justify-center h-6 w-6 rounded-full bg-button-secondary"
            onClick={() => setOpen(false)}
          >
            <XIcon className="text-text-icon-secondary" />
          </div>
        </div>
        <div className="px-4 pt-4 pb-5">
          <Card className="flex flex-col rounded-xl pl-4 py-[11px]">
            <div className="flex flex-col space-y-[12px]">
              {LANGUAGES.map((item, index) => (
                <div key={item}>
                  {index > 0 && <Separator className="mb-[12px] mt-2" />}
                  <div
                    className="flex flex-row items-center justify-between"
                    onClick={() => pick(item)}
                  >
                    <p className="text-body-base-regular text-text-primary">
                      {LANGUAGE_NAMES[item]}
                    </p>
                    {lang === item && (
                      <div className="pr-[13px]">
                        <CheckIcon className="text-brand-green" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
        {applyOnSelect ? (
          <div className="pb-safe-bottom pt-2" />
        ) : (
          <div className="w-full px-4 pb-7">
            <Button
              className="h-12 w-full bg-button-primary rounded-xl"
              onClick={changeLanguage}
            >
              <p className="text-body-base-semibold text-text-white">
                {LANGUAGE_DRAWER_LABELS[lang]?.save ?? t('save')}
              </p>
            </Button>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};
